import { Autocomplete, Box, Button, CardActions, CardContent, Chip, CircularProgress, Grid, InputAdornment, MenuItem, OutlinedInput, Select, Stack, styled, TextField, Typography, useTheme } from "@mui/material";
import { countries, languages } from 'countries-list'
import { useEffect, useState } from "react";
import AvatarDropzone from "./AvatarDropzone";
import { BlockPicker } from 'react-color';
import { Formik } from 'formik';
import MainCard from "components/MainCard";
import * as Yup from 'yup';
import { InfoCircle } from "iconsax-react";
import useApi from "hooks/useApi";
import ErrorTooltip from "components/ErrorTooltip";
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';

const SGrid = styled(Grid)({ display: 'flex', textAlign: 'right', alignItems: 'center', justifyContent: 'end'});

const AdornmentSelect = styled(Select)({ 
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    minWidth: '90px',
    maxWidth: '90px',
});

const StyledBlockPicker = styled(BlockPicker)(({ theme }) => ({
    marginTop: '10px',
    '&.block-picker': {
      display: 'flex !important',
      width: '100% !important',
      alignItems: 'start !important',
      boxShadow: 'none !important',
      background: 'transparent !important',
      '& div': {
        '&:nth-child(2)': {
          height: '55px !important',
          minWidth: '100px',
          borderRadius: '10px !important',
          padding: '10px !important',
        },
        '&:nth-child(3)': {
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
          '& div': {
            '&:nth-child(2)': {
              paddingLeft: '0 !important',
              paddingTop: '0px !important',
              paddingBottom: '0px !important',
            },
          },
          '& span': {
            '& div': {
              height: '12px !important',
              width: '24px !important',
              marginRight: '5px !important',
              marginBottom: '5px !important'
            }
          }
        }
      }
    }
  }));

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const defaultValue = {
    name: '',
    description: '',
    email: '',
    countryCode: '',
    phone: '',
    theme: {
        color_palatte: {
            primary: '#1d5c8e',
            primary_dark: '#143d5e',
            accent: '#ff9800',
            accent_dark: '#c66900',
            secondary: '#1da67c',
            secondary_dark: '#147f5c',
            text_color: '#1a1a1a',
            text_color_light: '#ffffff'
        },
        icons: {
            logo_path: {
                file_name: '',
                file_path: '',
                mime_type: '',
                public_url: '',
                url: ''
            },
            logo_light_path: {
                file_name: '',
                file_path: '',
                mime_type: '',
                public_url: '',
                url: ''
            },
            fav_icon_path: {
                file_name: '',
                file_path: '',
                mime_type: '',
                public_url: '',
                url: ''
            },
            square_logo_path: {
                file_name: '',
                file_path: '',
                mime_type: '',
                public_url: '',
                url: ''
            }
        }
    },
    languages: [],
    domain: ''
};

const ClientForm = ( { selected, resetForm, clientUid }: any) => {
    const theme = useTheme();
    const hasClientEditPermission = useHasPermission(PERMISSIONS.ADD_EDIT_CLIENT);
    
    const [phoneCodes, setPhoneCodes] = useState<string[]>([]);
    const [languageCodes, setLanguageCodes] = useState<any[]>([]);
    const [fixedOptions] = useState<any>([{ ...languages['en'], iso2: 'en' }]);
    const [selectedLanguages, setSelectedLanguages] = useState<any>([{ ...languages['en'], iso2: 'en' }]);
    const { createClient, showToast, base64Decode, updateClient } = useApi();
    const [initialValues, setInitialValues] = useState<typeof defaultValue | null>(null);

    useEffect(() => {
        let allCountries: any = { ...countries };
        let codes = Object.keys(countries).map((countryCode: string) => {
            let cData = allCountries[countryCode];
            return cData.phone;
        })
        
        codes = codes.flat();
        codes.sort((a, b) => Number(a) - Number(b));
        codes = codes.map(code => `+${code}`);
        const uniqueArr = [...new Set(codes)];
        setPhoneCodes(uniqueArr);

        
        let allLanguages: any = {...languages};
        
        let langCodes = Object.keys(allLanguages).map((langCode: string) => {
            let lData = allLanguages[langCode];
            return { ...lData, iso2: langCode };
        })
        
        setLanguageCodes(langCodes);

    }, []);

    useEffect(() => {
        // languageCodes

        if(selected && languageCodes){
            let {
                name,
                description,
                email,
                countryCode,
                phone,
                theme,
                languages,
                domain
            } = selected;

            let defaultLanguages = fixedOptions.map((el: any) => el.iso2);
            let selectedLanguages: any = languageCodes.filter(lang => (languages.includes(lang.iso2) && !defaultLanguages.includes(lang.iso2)));

            setSelectedLanguages([ ...fixedOptions, ...selectedLanguages ])
            
            setInitialValues({
                name,
                description,
                email: base64Decode(email),
                countryCode,
                phone: base64Decode(phone),
                theme,
                languages: [ ...fixedOptions, ...selectedLanguages ] as any,
                domain
            });

        }else{
            setInitialValues({ ...defaultValue, languages: fixedOptions });
        }
        
    }, [selected, languageCodes])

    const onClientFormSubmit = (formData: any, setSubmitting: any): void => {
        delete formData.theme?.icons?.logo_path.url;
        delete formData.theme?.icons?.logo_light_path.url;
        delete formData.theme?.icons?.fav_icon_path.url;
        delete formData.theme?.icons?.square_logo_path.url;

        formData.languages = selectedLanguages.map((v: any) => v.iso2);
        if(clientUid){
            updateClientApi(formData, setSubmitting);
        }else{
            createClientApi(formData, setSubmitting);
        }
    }

    const createClientApi = async (payload: any, setSubmitting: any) => {
        try{
            const response = await createClient(payload);
            const { success, message } = response;
            if(success){
                showToast(`Client has been created`, 'success');
                resetForm();
            }else {
                showToast(message, 'error');
            }
            setSubmitting(false);
        }catch(error: any){
            showToast(error.message, 'error');
            setSubmitting(false);
        }
    }

    const updateClientApi = async (payload: any, setSubmitting: any) => {
        try{
            const response = await updateClient(payload, clientUid);
            const { success, message } = response;
            if(success){
                showToast(`Client has been updated`, 'success');
                resetForm();
            }else {
                showToast(message, 'error');
            }
            setSubmitting(false);
        }catch(error: any){
            showToast(error.message, 'error');
            setSubmitting(false);
        }
    }

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        description: Yup.string().required('Description is required'),
        email: Yup.string().required('Email is required').matches(emailRegex, "Invalid email address"),
        countryCode: Yup.string().required('Country code is required'),
        phone: Yup.string().required('Phone number is required'),
        domain: Yup.string().required('Domain is required'),
        theme: Yup.object({
            color_palatte: Yup.object({
                primary: Yup.string().required('Primary Colour is required'),
                primary_dark: Yup.string().required('Primary Dark Colour is required'),
                accent: Yup.string().required('Accent Colour is required'),
                accent_dark: Yup.string().required('Accent Dark Colour is required'),
                secondary: Yup.string().required('Secondary Colour is required'),
                secondary_dark: Yup.string().required('Secondary Dark Colour is required'),
                text_color: Yup.string().required('Text Colour is required'),
                text_color_light: Yup.string().required('Text Light Colour is required'),
            }),
            icons: Yup.object({
                logo_path: Yup.object({
                    file_name: Yup.string().required('Logo is required'),
                    file_path: Yup.string().required('Logo is required'),
                    mime_type: Yup.string().required('Logo is required'),
                    public_url: Yup.string().required('Logo is required'),
                    url: Yup.string().required('Invalid Logo'),
                }),
                logo_light_path: Yup.object({
                    file_name: Yup.string().required('Logo Light is required'),
                    file_path: Yup.string().required('Logo Light is required'),
                    mime_type: Yup.string().required('Logo Light is required'),
                    public_url: Yup.string().required('Logo Light is required'),
                    url: Yup.string().required('Invalid Logo Light'),
                }),
                fav_icon_path: Yup.object({
                    file_name: Yup.string().required('Fav icon is required'),
                    file_path: Yup.string().required('Fav icon is required'),
                    mime_type: Yup.string().required('Fav icon is required'),
                    public_url: Yup.string().required('Fav icon is required'),
                    url: Yup.string().required('Invalid Fav icon'),
                }),
                square_logo_path: Yup.object({
                    file_name: Yup.string().required('App icon is required'),
                    file_path: Yup.string().required('App icon is required'),
                    mime_type: Yup.string().required('App icon is required'),
                    public_url: Yup.string().required('App icon is required'),
                    url: Yup.string().required('Invalid App icon'),
                }),
            }),
        })
    });

    return (
        initialValues && (
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting }) => {
                    let deepCopy = JSON.parse(JSON.stringify(values));
                    onClientFormSubmit(deepCopy, setSubmitting);
                }}
                >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    setFieldValue,
                    setFieldError,
                    setFieldTouched,
                    validateField
                }) => (
                    <form onSubmit={handleSubmit}>
                        <MainCard sx={{
                            bgcolor: 'transparent',
                            border: 0,
                            margin: 0,
                            padding: 0,
                            width: '100%'
                        }}>
                            <CardContent sx={{ padding: 0, margin: 0}}>
                                <Box sx={{ py: 0, pr: 2, width: '100%', height: '100%' }}>
                                    <Grid container sx={{ width: '100%' }} rowGap={3}>
                                        <SGrid item xs={3}>
                                            Client Name :&nbsp;
                                        </SGrid>
                                        <Grid item xs={9}>
                                            <OutlinedInput
                                                name="name"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                fullWidth
                                                size='small'
                                                placeholder="Enter Client Name"
                                                error={(touched.name && errors.name) ? true : false}
                                                defaultValue={values?.name}
                                                endAdornment={
                                                    (touched.name && errors.name) && <InputAdornment position="end">
                                                        <ErrorTooltip title={errors.name} open={(touched.name && errors.name) ? true : false} >
                                                            <InfoCircle size="14px" color={theme.palette.error.main} />
                                                        </ErrorTooltip>
                                                    </InputAdornment>
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={3} display={'flex'} justifyContent={'end'}>
                                            Description :&nbsp;
                                        </Grid>
                                        <Grid item xs={9}>
                                            <OutlinedInput
                                                name="description"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                size='small'
                                                placeholder="Enter Details about the client"
                                                error={(touched.description && errors.description) ? true : false}
                                                defaultValue={values?.description}
                                                endAdornment={
                                                    (touched.description && errors.description) && <InputAdornment position="end">
                                                        <ErrorTooltip title={errors.description} open={(touched.description && errors.description) ? true : false} >
                                                            <InfoCircle size="14px" color={theme.palette.error.main} />
                                                        </ErrorTooltip>
                                                    </InputAdornment>
                                                }
                                            />
                                        </Grid>

                                        <SGrid item xs={3}>
                                            Email Address :&nbsp;
                                        </SGrid>
                                        <Grid item xs={9}>
                                            <OutlinedInput
                                                name="email"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                fullWidth
                                                size='small'
                                                placeholder="Enter Email address"
                                                error={(touched.email && errors.email) ? true : false}
                                                defaultValue={values?.email}
                                                endAdornment={
                                                    (touched.email && errors.email) && <InputAdornment position="end">
                                                        <ErrorTooltip title={errors.email} open={(touched.email && errors.email) ? true : false} >
                                                            <InfoCircle size="14px" color={theme.palette.error.main} />
                                                        </ErrorTooltip>
                                                    </InputAdornment>
                                                }
                                            />
                                        </Grid>

                                        <SGrid item xs={3}>
                                            Mobile Number :&nbsp;
                                        </SGrid>
                                        <Grid item xs={9}>
                                            <OutlinedInput
                                                name="phone"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                fullWidth
                                                size='small'
                                                placeholder="Enter Phone number"
                                                sx={{ '&.MuiInputBase-root': { pl: '0' } }}
                                                defaultValue={values?.phone}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <AdornmentSelect
                                                            name="countryCode"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            size="small"
                                                            labelId="demo-simple-select-label"
                                                            id="demo-simple-select"
                                                            label="Age"
                                                            defaultValue={values?.countryCode}
                                                        >
                                                            {phoneCodes.map(code => <MenuItem value={code}>{code}</MenuItem>)}
                                                        </AdornmentSelect>
                                                    </InputAdornment>
                                                }
                                                error={((touched.countryCode && errors.countryCode) || (touched.phone && errors.phone)) ? true : false}
                                                endAdornment={
                                                    ((touched.countryCode && errors.countryCode) || (touched.phone && errors.phone)) && <InputAdornment position="end">
                                                        <ErrorTooltip title={errors.countryCode || errors.phone} open={((touched.countryCode && errors.countryCode) || (touched.phone && errors.phone)) ? true : false} >
                                                            <InfoCircle size="14px" color={theme.palette.error.main} />
                                                        </ErrorTooltip>
                                                    </InputAdornment>
                                                }
                                            />
                                        </Grid>

                                        <SGrid item xs={3}>
                                            Languages :&nbsp;
                                        </SGrid>
                                        <Grid item xs={9}>
                                            <Autocomplete
                                                multiple
                                                fullWidth
                                                onChange={(event, newValue) => {
                                                    setSelectedLanguages([
                                                        ...fixedOptions,
                                                        ...newValue.filter((option) => !fixedOptions.includes(option)),
                                                    ]);
                                                }}
                                                value={selectedLanguages}
                                                sx={{ width: '100% !important' }}
                                                options={languageCodes}
                                                getOptionLabel={(option) => `${option.name} (${option.native})`}
                                                renderTags={(tagValue, getTagProps) =>
                                                        tagValue.map((option, index) => {
                                                        const { key, ...tagProps } = getTagProps({ index });
                                                        return (
                                                            <Chip
                                                                key={key}
                                                                color="primary"
                                                                label={`${option.name} (${option.native})`}
                                                                {...tagProps}
                                                                disabled={fixedOptions.includes(option)}
                                                            />
                                                        );
                                                    })
                                                }
                                                style={{ width: 500 }}
                                                renderInput={(params) => (
                                                    <TextField {...params} placeholder="Languages" />
                                                )}
                                                />
                                        </Grid>
                                        <SGrid item xs={3}>
                                            Domain :&nbsp;
                                        </SGrid>
                                        <Grid item xs={9}>
                                            <OutlinedInput
                                                name="domain"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                fullWidth
                                                size='small'
                                                placeholder="Enter Domain address"
                                                error={(touched.domain && errors.domain) ? true : false}
                                                defaultValue={values?.domain}
                                                endAdornment={
                                                    (touched.domain && errors.domain) && <InputAdornment position="end">
                                                        <ErrorTooltip title={errors.domain} open={(touched.domain && errors.domain) ? true : false} >
                                                            <InfoCircle size="14px" color={theme.palette.error.main} />
                                                        </ErrorTooltip>
                                                    </InputAdornment>
                                                }
                                            />
                                        </Grid>
                                        <SGrid item xs={1}>
                                            
                                        </SGrid>
                                        <Grid item xs={2.75}>
                                            <Stack spacing={1.5} alignItems="center">
                                                <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                    Logo {
                                                        errors.theme?.icons?.logo_path?.url && (
                                                            <>
                                                            &nbsp;
                                                            <ErrorTooltip title={errors.theme?.icons?.logo_path?.url} open={errors.theme?.icons?.logo_path?.url ? true : false} >
                                                                    <InfoCircle size="14px" color={theme.palette.error.main} />
                                                                </ErrorTooltip>
                                                            </>
                                                        )
                                                    }
                                                </Typography>
                                                <AvatarDropzone
                                                    path={'logo_path'}
                                                    url={values?.theme?.icons?.logo_path?.url}
                                                    validation={{
                                                        width: 150,
                                                        height: 50,
                                                        ratio: 3/1,
                                                        fileSizeInMb: 1
                                                    }}
                                                    setFieldValue={setFieldValue}
                                                    setTouched={setFieldTouched}
                                                    setErrors={setFieldError}
                                                    validateField={validateField}
                                                />
                                                <Stack spacing={0}>
                                                    <Typography align="center" variant="caption" sx={{color:"text.dark"}}>
                                                    <Typography align="center" variant="body2" sx={{color:"text.dark"}}>
                                                        *.png, *.svg
                                                    </Typography>
                                                    1mb, W: 150px, H: 50px
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={2.75}>
                                            <Stack spacing={1.5} alignItems="center">
                                                <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                    Logo Light {
                                                        errors.theme?.icons?.logo_light_path?.url && (
                                                            <>
                                                            &nbsp;
                                                            <ErrorTooltip title={errors.theme?.icons?.logo_light_path?.url} open={errors.theme?.icons?.logo_light_path?.url ? true : false} >
                                                                    <InfoCircle size="14px" color={theme.palette.error.main} />
                                                                </ErrorTooltip>
                                                            </>
                                                        )
                                                    }
                                                </Typography>
                                                <AvatarDropzone
                                                    path={'logo_light_path'}
                                                    url={values?.theme?.icons?.logo_light_path?.url}
                                                    validation={{
                                                        width: 150,
                                                        height: 50,
                                                        ratio: 3/1,
                                                        fileSizeInMb: 1
                                                    }}
                                                    setFieldValue={setFieldValue}
                                                    setTouched={setFieldTouched}
                                                    setErrors={setFieldError}
                                                    validateField={validateField}
                                                />
                                                <Stack spacing={0}>
                                                    <Typography align="center" variant="caption" sx={{color:"text.dark"}}>
                                                    <Typography align="center" variant="body2" sx={{color:"text.dark"}}>
                                                        *.png, *.svg
                                                    </Typography>
                                                    1mb, W: 150px, H: 50px
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <Grid item xs={2.75}>
                                            <Stack spacing={1.5} alignItems="center">
                                                <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                    Favicon {
                                                        errors.theme?.icons?.fav_icon_path?.url && (
                                                            <>
                                                            &nbsp;
                                                            <ErrorTooltip title={errors.theme?.icons?.fav_icon_path?.url} open={errors.theme?.icons?.fav_icon_path?.url ? true : false} >
                                                                    <InfoCircle size="14px" color={theme.palette.error.main} />
                                                                </ErrorTooltip>
                                                            </>
                                                        )
                                                    }
                                                </Typography>
                                                <AvatarDropzone
                                                    path={'fav_icon_path'}
                                                    url={values?.theme?.icons?.fav_icon_path?.url}
                                                    validation={{
                                                        width: 32,
                                                        height: 32,
                                                        ratio: 1/1,
                                                        fileSizeInMb: 1
                                                    }}
                                                    setFieldValue={setFieldValue}
                                                    setTouched={setFieldTouched}
                                                    setErrors={setFieldError}
                                                    validateField={validateField}
                                                />
                                                <Stack spacing={0}>
                                                    <Typography align="center" variant="caption" sx={{color:"text.dark"}}>
                                                    <Typography align="center" variant="body2" sx={{color:"text.dark"}}>
                                                        *.png, *.svg
                                                    </Typography>
                                                    1mb, W: 32px, H: 32px
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={2.75}>
                                            <Stack spacing={1.5} alignItems="center">
                                                <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                    App Icon {
                                                        errors.theme?.icons?.square_logo_path?.url && (
                                                            <>
                                                            &nbsp;
                                                                <ErrorTooltip title={errors.theme?.icons?.square_logo_path?.url} open={errors.theme?.icons?.square_logo_path?.url ? true : false} >
                                                                    <InfoCircle size="14px" color={theme.palette.error.main} />
                                                                </ErrorTooltip>
                                                            </>
                                                        )
                                                    }
                                                </Typography>
                                                <AvatarDropzone
                                                    path={'square_logo_path'}
                                                    url={values?.theme?.icons?.square_logo_path?.url}
                                                    validation={{
                                                        width: 512,
                                                        height: 512,
                                                        ratio: 1/1,
                                                        fileSizeInMb: 1
                                                    }}
                                                    setFieldValue={setFieldValue}
                                                    setTouched={setFieldTouched}
                                                    setErrors={setFieldError}
                                                    validateField={validateField}
                                                />
                                                <Stack spacing={0}>
                                                    <Typography align="center" variant="caption" sx={{color:"text.dark"}}>
                                                    <Typography align="center" variant="body2" sx={{color:"text.dark"}}>
                                                        *.png, *.svg
                                                    </Typography>
                                                    1mb, W: 512px, H: 512px
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Grid>

                                        <SGrid item xs={1}></SGrid>
                                        <Grid item xs={5.5}>
                                            <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                Primary Colour
                                                {
                                                    errors.theme?.color_palatte?.primary && (
                                                        <>
                                                        &nbsp;
                                                            <ErrorTooltip title={errors.theme?.color_palatte?.primary} open={(touched.theme?.color_palatte?.primary && errors.theme?.color_palatte?.primary) ? true : false} >
                                                                <InfoCircle size="14px" color={theme.palette.error.main} />
                                                            </ErrorTooltip>
                                                        </>
                                                    )
                                                }
                                            </Typography>
                                            <StyledBlockPicker
                                                onChange={(color) => {
                                                    setFieldValue('theme.color_palatte.primary', color.hex);
                                                }}
                                                color={values?.theme?.color_palatte?.primary}
                                                triangle={'hide'}
                                            />
                                        </Grid>
                                        <Grid item xs={5.5}>
                                            <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                Primary Dark Colour
                                                {
                                                    errors.theme?.color_palatte?.primary_dark && (
                                                        <>
                                                        &nbsp;
                                                            <ErrorTooltip title={errors.theme?.color_palatte?.primary_dark} open={(touched.theme?.color_palatte?.primary_dark && errors.theme?.color_palatte?.primary_dark) ? true : false} >
                                                                <InfoCircle size="14px" color={theme.palette.error.main} />
                                                            </ErrorTooltip>
                                                        </>
                                                    )
                                                }
                                            </Typography>
                                            <StyledBlockPicker
                                                onChange={(color) => {
                                                    setFieldValue('theme.color_palatte.primary_dark', color.hex);
                                                }}
                                                color={values?.theme?.color_palatte?.primary_dark}
                                                triangle={'hide'}
                                            />
                                        </Grid>

                                        <SGrid item xs={1}></SGrid>
                                        <Grid item xs={5.5}>
                                            <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                Secondary Colour
                                                {
                                                    errors.theme?.color_palatte?.secondary && (
                                                        <>
                                                        &nbsp;
                                                            <ErrorTooltip title={errors.theme?.color_palatte?.secondary} open={(touched.theme?.color_palatte?.secondary && errors.theme?.color_palatte?.secondary) ? true : false} >
                                                                <InfoCircle size="14px" color={theme.palette.error.main} />
                                                            </ErrorTooltip>
                                                        </>
                                                    )
                                                }
                                            </Typography>
                                            <StyledBlockPicker
                                                onChange={(color) => {
                                                    setFieldValue('theme.color_palatte.secondary', color.hex);
                                                }}
                                                color={values?.theme?.color_palatte?.secondary}
                                                triangle={'hide'}
                                            />
                                        </Grid>
                                        <Grid item xs={5.5}>
                                            <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                Secondary Dark Colour
                                                {
                                                    errors.theme?.color_palatte?.secondary_dark && (
                                                        <>
                                                        &nbsp;
                                                            <ErrorTooltip title={errors.theme?.color_palatte?.secondary_dark} open={(touched.theme?.color_palatte?.secondary_dark && errors.theme?.color_palatte?.secondary_dark) ? true : false} >
                                                                <InfoCircle size="14px" color={theme.palette.error.main} />
                                                            </ErrorTooltip>
                                                        </>
                                                    )
                                                }
                                            </Typography>
                                            <StyledBlockPicker
                                                onChange={(color) => {
                                                    setFieldValue('theme.color_palatte.secondary_dark', color.hex);
                                                }}
                                                color={values?.theme?.color_palatte?.secondary_dark}
                                                triangle={'hide'}
                                            />
                                        </Grid>

                                        <SGrid item xs={1}></SGrid>
                                        <Grid item xs={5.5}>
                                            <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                Accent Colour
                                                {
                                                    errors.theme?.color_palatte?.accent && (
                                                        <>
                                                        &nbsp;
                                                            <ErrorTooltip title={errors.theme?.color_palatte?.accent} open={(touched.theme?.color_palatte?.accent && errors.theme?.color_palatte?.accent) ? true : false} >
                                                                <InfoCircle size="14px" color={theme.palette.error.main} />
                                                            </ErrorTooltip>
                                                        </>
                                                    )
                                                }
                                            </Typography>
                                            <StyledBlockPicker
                                                onChange={(color) => {
                                                    setFieldValue('theme.color_palatte.accent', color.hex);
                                                }}
                                                color={values?.theme?.color_palatte?.accent}
                                                triangle={'hide'}
                                            />
                                        </Grid>
                                        <Grid item xs={5.5}>
                                            <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                Accent Dark Colour
                                                {
                                                    errors.theme?.color_palatte?.accent_dark && (
                                                        <>
                                                        &nbsp;
                                                            <ErrorTooltip title={errors.theme?.color_palatte?.accent_dark} open={(touched.theme?.color_palatte?.accent_dark && errors.theme?.color_palatte?.accent_dark) ? true : false} >
                                                                <InfoCircle size="14px" color={theme.palette.error.main} />
                                                            </ErrorTooltip>
                                                        </>
                                                    )
                                                }
                                            </Typography>
                                            <StyledBlockPicker
                                                onChange={(color) => {
                                                    setFieldValue('theme.color_palatte.accent_dark', color.hex);
                                                }}
                                                color={values?.theme?.color_palatte?.accent_dark}
                                                triangle={'hide'}
                                            />
                                        </Grid>

                                        <SGrid item xs={1}></SGrid>
                                        <Grid item xs={5.5}>
                                            <Typography align="center" variant="subtitle1" sx={{color:"text.dark"}} display={'flex'} alignItems={'center'}>
                                                Text Colour
                                                {
                                                    errors.theme?.color_palatte?.text_color && (
                                                        <>
                                                        &nbsp;
                                                            <ErrorTooltip title={errors.theme?.color_palatte?.text_color} open={(touched.theme?.color_palatte?.text_color && errors.theme?.color_palatte?.text_color) ? true : false} >
                                                                <InfoCircle size="14px" color={theme.palette.error.main} />
                                                            </ErrorTooltip>
                                                        </>
                                                    )
                                                }
                                            </Typography>
                                            <StyledBlockPicker
                                                onChange={(color) => {
                                                    setFieldValue('theme.color_palatte.text_color', color.hex);
                                                }}
                                                color={values?.theme?.color_palatte?.text_color}
                                                triangle={'hide'}
                                            />
                                        </Grid>
                                        <Grid item xs={5.5}>
                                            <Typography align="left" variant="subtitle1" sx={{color:"text.dark"}}>
                                                Text Colour Light
                                                {
                                                    errors.theme?.color_palatte?.text_color_light && (
                                                        <>
                                                        &nbsp;
                                                            <ErrorTooltip title={errors.theme?.color_palatte?.text_color_light} open={(touched.theme?.color_palatte?.text_color_light && errors.theme?.color_palatte?.text_color_light) ? true : false} >
                                                                <InfoCircle size="14px" color={theme.palette.error.main} />
                                                            </ErrorTooltip>
                                                        </>
                                                    )
                                                }
                                            </Typography>
                                            <StyledBlockPicker
                                                onChange={(color) => {
                                                    setFieldValue('theme.color_palatte.text_color_light', color.hex);
                                                }}
                                                color={values?.theme?.color_palatte?.text_color_light}
                                                triangle={'hide'}
                                            />
                                        </Grid>

                                    </Grid>
                                </Box>
                            </CardContent>
                            <CardActions sx={{ borderTop: '1px solid rgba(219, 224, 229, 0.65)', justifyContent: 'end', pt: 2, pb: 0 }}>
                                <Button
                                    disabled={isSubmitting || !hasClientEditPermission}
                                    variant='contained'
                                    
                                    type='submit'
                                    sx={{
                                        transition: 'all 0.5s ease-out',
                                        color:"text.main",
                                        bgcolor:"secondary.dark",
                                        '&:hover': {
                    bgcolor: 'secondary.main'
                  },
                                    }}
                                    startIcon={
                                        isSubmitting && <CircularProgress color="primary" size="sm" />
                                    }
                                >
                                    Save
                                </Button>
                            </CardActions>
                        </MainCard>
                    </form>
                )}
            </Formik>
        )
    )
}

export default ClientForm;